// Copyright (c) Docugami, Inc. All rights reserved.

import { createFolderId } from "components/file_management/file_helpers";
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { ChunkInstance } from "networking/api";
import { useCallback } from "react";
import { AppDispatch, AppState } from "store";
import { reportErrorEncountered } from "store/authentication/actions";
import { dgmlSlice } from "store/dgml/dgmlSlice";
import { Label } from "store/dgml/dgmlTypes";
import { addLabel, getSpanIdForTokenRange } from "../store/dgml/dgmlActions";
import { useDocumentProperties } from "./documentProperties";
import { selectLabelState } from "store/frontend/selectors";

export interface ILabelChunk {
    labelChunk(chunkInstance: ChunkInstance, label: string): Promise<ChunkInstance>;
    getRecommendedTags(chunkInstance: ChunkInstance): Label[];
    getChunkLabel(chunkInstance: ChunkInstance): string;
}

export function useLabelChunk(docSetId: string, documentId: string): ILabelChunk {
    const { data: labels } = useAppSelector(selectLabelState(docSetId));
    const dispatch = useAppDispatch();

    const { updateProperty } = useDocumentProperties(documentId);

    const labelChunk = useCallback(async (chunkInstance: ChunkInstance, targetLabel: string): Promise<ChunkInstance> => {
        try {
            const spanId = !!chunkInstance.spanId ? chunkInstance.spanId : (chunkInstance?.chunk?.spanId ?? dispatch(getSpanIdForTokenRange(documentId, chunkInstance.tokenRange))?.value);
            if (!spanId) {
                throw new Error("Could not determine spanId for label");
            }

            if (!!chunkInstance.addedChunkInfo) {
                await updateProperty(spanId, { label: targetLabel });
                return chunkInstance;
            } else {
                return await addLabel(docSetId, spanId, targetLabel);
            }
        }
        catch (error) {
            dispatch(reportErrorEncountered(`Failed to label chunk`, error as Error));
            throw error;
        }
    }, [dispatch, docSetId, documentId, updateProperty]);

    const getRecommendedTags = useCallback((chunkInstance: ChunkInstance) => {
        const currentLabelItem = labels?.entities[chunkInstance.labelId ?? '']
        return getRecommendedTagsHelper(chunkInstance, currentLabelItem?.label ?? '');
    }, [labels?.entities]);

    const getChunkLabelCallback = useCallback((instance: ChunkInstance) => {
        return dispatch(getChunkLabel(instance));
    }, [dispatch]);

    return {
        labelChunk: labelChunk,
        getRecommendedTags: getRecommendedTags,
        getChunkLabel: getChunkLabelCallback
    }
}

export const hasSemanticLabel = (instance: ChunkInstance): boolean => {
    if (!!instance.labelId) {
        return true;
    }

    const mostSemanticLabel = getRecommendedTagsHelper(instance, '')[0];
    if (!!mostSemanticLabel) {
        return getLabelRank(mostSemanticLabel) < namespaceToRank["SEMANTIC_BORDER"];
    }

    return false;
}

export const getChunkLabel = (instance: ChunkInstance) => (dispatch: AppDispatch, getState: () => AppState) => {
    const folderId = createFolderId(instance.folderId);
    const labels = selectLabelState(folderId.clusterId)(getState()).data?.entities;
    if (!!instance.labelId && !!labels) {
        const label = labels[instance.labelId]
        if (!!label) {
            return label?.label;
        }
    }

    if (instance.chunk?.labels[0]?.toolId === "cp") {
        // There is a user label on the chunk
        return instance.chunk.labels[0].value;
    }

    if (!!instance.addedChunkInfo) {
        const addedChunkSchemas = dgmlSlice.endpoints.getAddedChunkSchema.select({ docsetId: folderId.clusterId })(getState()).data;
        const addedChunkDefinition = addedChunkSchemas?.definitions.find(x => x.id === instance.addedChunkInfo!.propertyId);
        return addedChunkDefinition?.label ?? '';
    }

    const id = instance.spanId!;
    const state = getState();
    const selectedId = state.sdv.document?.selection?.id;
    if (id === selectedId) {
        const recommendedLabel = state.sdv.document?.selection?.defaultLabel;
        if (!!recommendedLabel) {
            return recommendedLabel;
        }
    }

    return getRecommendedTagsHelper(instance, '')[0]?.value ?? '';
}

export function getRecommendedTagsHelper(chunkInstance: ChunkInstance, chunkLabel: string): Label[] {
    if (!chunkInstance.chunk?.labels.length) {
        return []
    };

    const labels: Label[] = [];
    const set: Set<string> = new Set();
    const sorted = [...chunkInstance.chunk?.labels].sort((a, b) => getLabelRank(a) - getLabelRank(b));
    sorted.forEach(x => {
        const lowerValue = x.value.toLowerCase();
        if (!set.has(lowerValue) && x.toolId !== "cp" && lowerValue !== chunkLabel.toLowerCase()) {
            labels.push(x);
            set.add(lowerValue);
        }
    });

    return labels;
}

const namespaceToRank: { [key: string]: number } = {
    "cp" : 0,
    "dg-llm-qa" : 1,
    "dg-qa-red-labeler" : 3,
    "dg-chunk-ac" : 5,
    "dg-table-ac" : 5,
    "dg-zlqa" : 5,
    "cp-container" : 10,
    "cp-heading" : 10,
    "table_cell_labeler" : 10,
    "table_cell_labeler_ml" : 10,
    "table_labeler" : 10,
    "ingestion-tuple-labeler" : 10,
    "layout-table-cell-labeler" : 10,
    "cp-csl" : 15,
    "cp-lcl" : 15,
    "dg-xpath" : 16,
    "SEMANTIC_BORDER" : 18,
    "dg-distil-bert-green-ner" : 20,
    "dg-distil-bert-green-ner-medical" : 20,
    "cp-gaz" : 25,
    "cp-re" : 25,
    "cp-lp2-small" : 30,
    "cp-lp2-large" : 30,
};

function getLabelRank(label: Label): number {
    return namespaceToRank[label.toolId] ?? Infinity
}
